/**
 * One-click MCP install helpers.
 *
 * SendFast publishes the same HTTP MCP server (`/api/mcp` + Bearer auth) to
 * every host. The deeplink shapes differ per host though:
 *
 *  - Cursor: `cursor://anysphere.cursor-deeplink/mcp/install?name=…&config=<base64>`
 *    where the base64 payload is the *inner* server config object only
 *    (no `mcpServers` wrapper, no `name` key inside).
 *    https://cursor.com/docs/mcp/install-links
 *
 *  - VS Code (+ Insiders): `vscode:mcp/install?<urlencoded-json>` where the
 *    JSON includes `name`, `type: "http"`, `url`, `headers`.
 *    https://code.visualstudio.com/api/extension-guides/ai/mcp
 *
 *  - Claude Desktop: no protocol handler. We render the JSON snippet plus
 *    the OS-specific config file path so the user can paste-and-restart.
 *
 *  - Claude.ai (web): no deeplink either. Claude exposes "Custom
 *    Connectors" in Settings; the user pastes our URL + Bearer header.
 *
 * Two ways to get the API key into the deeplink:
 *
 *  - `installClient(client, { apiKey })` — caller has already minted the
 *    key (e.g. <RevealedKeyCard> in Settings).
 *  - `installClient(client, { autoMint: true })` — we POST `/api/keys` to
 *    the user's session and bake the plaintext into the deeplink. Returns
 *    the created key so the UI can also reveal it ("save it in case the
 *    deeplink fails"). Throws if the user is signed out or out of quota —
 *    callers handle that with a sign-in prompt or upgrade nudge.
 */

import { createApiKey, type CreatedApiKey } from './api-keys';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Production MCP endpoint. Hardcoded to sendfast.ai because deeplinks need
 * to point at a host the user's MCP client can actually reach — localhost
 * works in `npm run dev` but a Cursor deeplink minted from
 * `localhost:5173` would still install `https://sendfast.ai/api/mcp`,
 * which is what we want for every real install.
 */
export const MCP_ENDPOINT = 'https://sendfast.ai/api/mcp';

/** Placeholder used when the user is signed out — they have to swap it in manually. */
export const PLACEHOLDER_KEY = 'sf_live_REPLACE_ME';

/** Server name as it appears inside the host's MCP config. */
export const SERVER_NAME = 'sendfast';

// ---------------------------------------------------------------------------
// Client catalog
// ---------------------------------------------------------------------------

export type InstallClient = 'cursor' | 'vscode' | 'vscode-insiders' | 'claude-desktop' | 'claude-web';

export interface InstallClientMeta {
  id: InstallClient;
  /** Human-readable label used in buttons / cards. */
  label: string;
  /** True when the host supports a one-click protocol handler. */
  oneClick: boolean;
  /** Short description shown under the label on the install page. */
  blurb: string;
}

export const INSTALL_CLIENTS: InstallClientMeta[] = [
  {
    id: 'cursor',
    label: 'Cursor',
    oneClick: true,
    blurb: 'Opens Cursor and adds SendFast to your global MCP servers.',
  },
  {
    id: 'vscode',
    label: 'VS Code',
    oneClick: true,
    blurb: 'For GitHub Copilot Chat with MCP enabled.',
  },
  {
    id: 'vscode-insiders',
    label: 'VS Code Insiders',
    oneClick: true,
    blurb: 'Same as VS Code, for the Insiders build.',
  },
  {
    id: 'claude-desktop',
    label: 'Claude Desktop',
    oneClick: false,
    blurb: 'Paste a snippet into your config file and restart Claude.',
  },
  {
    id: 'claude-web',
    label: 'Claude.ai (web)',
    oneClick: false,
    blurb: 'Add SendFast as a Custom Connector in Settings → Connectors.',
  },
];

export const CLAUDE_DESKTOP_CONFIG_PATHS = {
  macos: '~/Library/Application Support/Claude/claude_desktop_config.json',
  windows: '%APPDATA%\\Claude\\claude_desktop_config.json',
  linux: '~/.config/Claude/claude_desktop_config.json',
};

// ---------------------------------------------------------------------------
// JSON snippets (copy-paste fallbacks)
// ---------------------------------------------------------------------------

/** The canonical MCP config snippet shown to humans. Used by every "copy" affordance. */
export function buildMcpConfigJson(apiKey: string | null): string {
  return JSON.stringify(
    {
      mcpServers: {
        [SERVER_NAME]: {
          url: MCP_ENDPOINT,
          headers: {
            Authorization: `Bearer ${apiKey ?? PLACEHOLDER_KEY}`,
          },
        },
      },
    },
    null,
    2,
  );
}

// ---------------------------------------------------------------------------
// Deeplink builders
// ---------------------------------------------------------------------------

/**
 * `cursor://anysphere.cursor-deeplink/mcp/install?name=<name>&config=<base64>`
 *
 * Cursor expects the *inner* server config (no wrapper, no `name` key) as
 * a base64-encoded JSON string. The wrapper is implicit in the deeplink.
 */
export function buildCursorDeeplink(apiKey: string | null): string {
  const config = {
    url: MCP_ENDPOINT,
    headers: {
      Authorization: `Bearer ${apiKey ?? PLACEHOLDER_KEY}`,
    },
  };
  const b64 = base64UrlSafe(JSON.stringify(config));
  return `cursor://anysphere.cursor-deeplink/mcp/install?name=${encodeURIComponent(SERVER_NAME)}&config=${b64}`;
}

/**
 * `vscode:mcp/install?<urlencoded-json>` (or `vscode-insiders:` for Insiders).
 *
 * VS Code expects the full descriptor — including `name` and `type` — as
 * a single URL-encoded JSON string in the query (no key=value).
 */
export function buildVSCodeDeeplink(
  apiKey: string | null,
  variant: 'vscode' | 'vscode-insiders' = 'vscode',
): string {
  const obj = {
    name: SERVER_NAME,
    type: 'http' as const,
    url: MCP_ENDPOINT,
    headers: {
      Authorization: `Bearer ${apiKey ?? PLACEHOLDER_KEY}`,
    },
  };
  const scheme = variant === 'vscode-insiders' ? 'vscode-insiders' : 'vscode';
  return `${scheme}:mcp/install?${encodeURIComponent(JSON.stringify(obj))}`;
}

/** Returns the deeplink URL or `null` for hosts with no protocol handler. */
export function buildDeeplink(client: InstallClient, apiKey: string | null): string | null {
  switch (client) {
    case 'cursor':
      return buildCursorDeeplink(apiKey);
    case 'vscode':
      return buildVSCodeDeeplink(apiKey, 'vscode');
    case 'vscode-insiders':
      return buildVSCodeDeeplink(apiKey, 'vscode-insiders');
    case 'claude-desktop':
    case 'claude-web':
      return null;
  }
}

// ---------------------------------------------------------------------------
// Public install API — the thing buttons call
// ---------------------------------------------------------------------------

export interface InstallOptions {
  /** Reuse a key the caller already has. Mutually exclusive with `autoMint`. */
  apiKey?: string;
  /** Mint a fresh key in the user's session before opening the deeplink. */
  autoMint?: boolean;
  /**
   * Optional name for the freshly minted key. Defaults to a per-client
   * label like "Cursor + VS Code — Nov 26, 2026".
   */
  keyName?: string;
  /** Skip the session cache and mint a brand-new key. Rarely needed. */
  forceFresh?: boolean;
}

export interface InstallResult {
  /** The deeplink we opened, or `null` for hosts with no deeplink. */
  url: string | null;
  /**
   * The minted key. Always populated for `autoMint: true`, even when we
   * pulled the value from the session cache (the field name is a slight
   * lie but it's the cleanest API for callers — they get a `CreatedApiKey`
   * shape they can render in their reveal card either way).
   */
  created?: CreatedApiKey;
  /** True when the key in `created` came from the session cache. */
  reused?: boolean;
}

/**
 * sessionStorage key holding the most recent auto-minted plaintext for
 * this tab session. We cache the plaintext (not just the prefix) because
 * subsequent install clicks on the same page need to bake it back into
 * a fresh deeplink — and plaintext only exists at creation time.
 *
 * Clearing happens on tab close (sessionStorage scope) and on sign-out
 * (handled by /sign-out clearing all client storage isn't needed; if a
 * second user signs in, the user_id check below misses and a fresh key
 * gets minted for them).
 */
const SESSION_KEY_CACHE = 'sf:install-key';

interface CachedKey {
  userId: string;
  created: CreatedApiKey;
}

/**
 * Open the host's MCP install deeplink. For hosts that don't have one
 * (Claude Desktop / Claude.ai) this is a no-op; the caller should render
 * a copy-snippet card instead.
 *
 * `autoMint` is idempotent within a single tab session — the first call
 * mints + caches a plaintext key in `sessionStorage`, and every
 * subsequent call reuses it. Without this, a user clicking
 * "Add to Cursor" then "Add to VS Code" on /install would burn 2 of
 * their (free-tier: 1) API key slots for one logical install.
 *
 * Errors:
 *  - `autoMint` + signed-out user → ApiError(401)
 *  - `autoMint` + at API key limit → ApiError(403/402); surface verbatim,
 *    do not retry.
 */
export async function installClient(
  client: InstallClient,
  opts: InstallOptions = {},
  /** Optional logged-in user id; gates the session cache. */
  currentUserId?: string,
): Promise<InstallResult> {
  let apiKey: string | null = null;
  let created: CreatedApiKey | undefined;
  let reused = false;

  if (opts.apiKey) {
    apiKey = opts.apiKey;
  } else if (opts.autoMint) {
    if (!opts.forceFresh && currentUserId) {
      const cached = readCachedKey(currentUserId);
      if (cached) {
        created = cached;
        apiKey = cached.key;
        reused = true;
      }
    }
    if (!apiKey) {
      const name = opts.keyName ?? defaultKeyName();
      created = await createApiKey(name);
      apiKey = created.key;
      if (currentUserId) writeCachedKey(currentUserId, created);
    }
  }

  const url = buildDeeplink(client, apiKey);
  if (url) {
    // window.location.assign() works for `cursor:` / `vscode:` schemes —
    // browsers hand the URL off to the OS without navigating away. If the
    // protocol handler isn't registered the click silently no-ops, which
    // is why the buttons also expose the snippet as a fallback.
    window.location.assign(url);
  }

  return { url, created, reused };
}

function readCachedKey(userId: string): CreatedApiKey | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(SESSION_KEY_CACHE);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedKey;
    if (parsed.userId !== userId) return null;
    return parsed.created;
  } catch {
    return null;
  }
}

function writeCachedKey(userId: string, created: CreatedApiKey): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(
      SESSION_KEY_CACHE,
      JSON.stringify({ userId, created } satisfies CachedKey),
    );
  } catch {
    /* QuotaExceeded etc — degrade silently, next install will re-mint. */
  }
}

/** Wipe the cached install key (used by sign-out). */
export function clearInstallKeyCache(): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(SESSION_KEY_CACHE);
  } catch {
    /* noop */
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function defaultKeyName(): string {
  const date = new Date().toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return `One-click install — ${date}`;
}

/**
 * Standard base64 (not base64url) of a UTF-8 string. Cursor's docs use
 * standard base64 in their examples, so we match that. We do `btoa` over
 * the URL-safe encoding of arbitrary unicode — `unescape(encodeURIComponent(...))`
 * is the canonical browser-side trick for that.
 */
function base64UrlSafe(s: string): string {
  // eslint-disable-next-line @typescript-eslint/no-deprecated -- btoa is fine in browsers
  return btoa(unescape(encodeURIComponent(s)));
}
